<template>
  <div ref="host" class="hero-spotlight" :class="{ 'is-visible': active && ready }" aria-hidden="true">
    <svg class="spotlight-light" :viewBox="`0 0 ${width} ${height}`" preserveAspectRatio="none" focusable="false">
      <defs>
        <linearGradient id="hero-beam-fade" x1="0" y1="0" :x2="geometry.length" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="white" stop-opacity=".36" />
          <stop offset=".36" stop-color="white" stop-opacity=".18" />
          <stop offset=".72" stop-color="white" stop-opacity=".10" />
          <stop offset="1" stop-color="white" stop-opacity="0" />
        </linearGradient>
        <filter id="hero-beam-softness" x="-35%" y="-100%" width="170%" height="300%" color-interpolation-filters="sRGB">
          <feGaussianBlur stdDeviation="16" />
        </filter>
      </defs>
      <g :transform="`translate(${geometry.sourceX} ${geometry.sourceY}) rotate(${geometry.angle})`">
        <path :d="geometry.beam" fill="url(#hero-beam-fade)" filter="url(#hero-beam-softness)" />
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'HeroSpotlight',
  props: { active: { type: Boolean, default: false } },
  data: () => ({ width: 1, height: 1, targetX: 0, targetY: 0, ready: false, frameId: null, sizeObserver: null, disposed: false }),
  computed: {
    geometry() {
      const sourceX = -Math.min(80, this.width * .08)
      const sourceY = -90
      const dx = this.targetX - sourceX
      const dy = this.targetY - sourceY
      const distance = Math.hypot(dx, dy)
      // Restore the original width while retaining a single soft-edged beam.
      const spread = Math.max(90, Math.min(220, this.width * .14))
      const length = distance + spread * .9
      const beam = `M 0 -10 L ${distance * .76} ${-spread * .74} Q ${length} ${-spread} ${length} 0 Q ${length} ${spread} ${distance * .76} ${spread * .74} L 0 10 Z`
      return { sourceX, sourceY, length, spread, angle: Math.atan2(dy, dx) * 180 / Math.PI, beam }
    }
  },
  watch: { active(value) { if (value) this.queueMeasure() } },
  mounted() {
    const hero = this.$refs.host.closest('.hero')
    this.sizeObserver = new ResizeObserver(this.queueMeasure)
    for (const element of [hero, hero?.querySelector('.hero-content'), hero?.querySelector('.hero-declaration')]) {
      if (element) this.sizeObserver.observe(element)
    }
    this.queueMeasure()
    // Font metrics can change the final line even if the viewport stays the same.
    document.fonts?.ready.then(this.queueMeasure).catch(() => {})
  },
  beforeUnmount() {
    this.disposed = true
    this.sizeObserver?.disconnect()
    if (this.frameId !== null) window.cancelAnimationFrame(this.frameId)
  },
  methods: {
    queueMeasure() {
      if (this.disposed || this.frameId !== null) return
      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null
        this.measure()
      })
    },
    measure() {
      const hero = this.$refs.host.closest('.hero')
      const declaration = hero?.querySelector('.hero-declaration')
      if (!hero || !declaration) return
      const bounds = hero.getBoundingClientRect()
      if (!bounds.width || !bounds.height) return
      const range = document.createRange()
      range.selectNodeContents(declaration)
      const lines = Array.from(range.getClientRects()).filter(rect => rect.width && rect.height)
      const lastLine = lines[lines.length - 1] || declaration.getBoundingClientRect()
      this.width = bounds.width
      this.height = bounds.height
      // Aim near the last words, including when the declaration wraps on mobile.
      this.targetX = Math.max(16, Math.min(bounds.width - 16, lastLine.right - bounds.left - Math.min(48, lastLine.width * .18)))
      this.targetY = Math.max(16, Math.min(bounds.height - 16, lastLine.top - bounds.top + lastLine.height / 2))
      this.ready = true
    }
  }
}
</script>

<style scoped>
.hero-spotlight{position:absolute;inset:0;z-index:-1;overflow:hidden;pointer-events:none;user-select:none;mix-blend-mode:screen;contain:paint}
.spotlight-light{display:block;width:100%;height:100%;opacity:0;transform-origin:top left}
.is-visible .spotlight-light{animation:spotlight-enter 1.6s cubic-bezier(.22,1,.36,1) .12s both}
@keyframes spotlight-enter{from{opacity:0;transform:translate(-18px,-24px) scale(.97)}to{opacity:1;transform:translate(0,0) scale(1)}}
@media(max-width:620px){.hero-spotlight{opacity:.72}}
@media(prefers-reduced-motion:reduce){.is-visible .spotlight-light{animation:none;opacity:1;transform:none}}
</style>
